import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, hasRole, imgUrl, timeLeft } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/desk/DeskLayout";
import { StatusChip } from "@/components/desk/StatusChip";
import { TrendBadge } from "@/components/news/TrendBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ACTIONS = {
  submit: { label: "Submit for approval", from: ["draft", "rejected", "on_hold", "withdrawn"], author: true },
  approve: { label: "Approve", from: ["submitted", "on_hold"] },
  publish: { label: "Publish now", from: ["draft", "submitted", "approved", "on_hold", "scheduled"] },
  schedule: { label: "Schedule", from: ["draft", "submitted", "approved", "on_hold"], input: "datetime" },
  unschedule: { label: "Disable schedule", from: ["scheduled"] },
  hold: { label: "Put on hold", from: ["submitted", "approved", "scheduled"], input: "feedback" },
  reject: { label: "Reject with feedback", from: ["submitted", "approved", "scheduled", "on_hold"], input: "feedback", required: true, danger: true },
  withdraw: { label: "Withdraw", from: ["published", "scheduled", "approved"], input: "feedback", danger: true },
  extend_trend: { label: "Extend trend timer", from: ["published"], input: "hours" },
};

const Preview = ({ s, lang }) => {
  const v = s[lang] || {};
  return (
    <div className={lang === "ta" ? "lang-ta" : ""} data-testid={`review-preview-${lang}`}>
      <h2 className="font-serif-head font-black text-2xl sm:text-3xl leading-tight">{v.headline || <span className="text-zinc-400">No {lang === "ta" ? "Tamil" : "English"} version</span>}</h2>
      {v.summary && <p className="mt-3 text-zinc-600">{v.summary}</p>}
      {v.content ? (
        <div className="mt-4 article-body text-[15px]" data-testid={`review-body-${lang}`} dangerouslySetInnerHTML={{ __html: v.content }} />
      ) : (
        <p className="mt-4 text-zinc-400">{v.headline ? "Content missing" : "This story is published in one language only."}</p>
      )}
    </div>
  );
};

export default function StoryReview() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [hist, setHist] = useState([]);
  const [cats, setCats] = useState([]);
  const [lang, setLang] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [input, setInput] = useState({ feedback: "", scheduled_at: "", hours: 12 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get(`/desk/stories/${id}`), api.get(`/desk/stories/${id}/history`)]);
    setS(a.data);
    setHist(b.data);
  }, [id]);

  useEffect(() => {
    load().catch(() => setS(false));
    api.get("/desk/meta").then((r) => setCats(r.data.categories));
  }, [load]);

  if (s === false) return <p data-testid="review-not-found" className="text-zinc-500">Story not found.</p>;
  if (!s) return <p className="text-zinc-400">Loading…</p>;

  const approver = hasRole(user, "news_approver");
  const tabLang = lang || (s.ta?.headline || !s.en?.headline ? "ta" : "en");
  const available = Object.entries(ACTIONS).filter(([k, a]) =>
    a.from.includes(s.status) && (a.author ? s.can_edit : approver) && !(k === "extend_trend" && !["hot", "breaking"].includes(s.trend_original)));

  const run = async (action) => {
    const a = ACTIONS[action];
    if (a.input && !dlg) {
      setInput({ feedback: "", scheduled_at: "", hours: 12 });
      setDlg(action);
      return;
    }
    setBusy(true);
    try {
      const body = { action, feedback: input.feedback, hours: Number(input.hours) || 0,
        scheduled_at: input.scheduled_at ? new Date(input.scheduled_at).toISOString() : null };
      await api.post(`/desk/stories/${id}/action`, body);
      toast.success(`${a.label} — done`);
      setDlg(null);
      await load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const cat = cats.find((c) => c.id === s.category_id);
  const da = dlg && ACTIONS[dlg];

  return (
    <div data-testid="story-review">
      <PageHead title="Story review" sub={`Created by ${s.author_name}${s.author_role === "reporter" ? " (external reporter)" : ""}`}>
        {s.status === "published" && (
          <Button asChild variant="outline" className="rounded-sm" data-testid="review-view-live"><a href={`/story/${s.slug}`} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1" /> View live</a></Button>
        )}
        {s.can_edit && (
          <Button asChild variant="outline" className="rounded-sm" data-testid="review-edit-btn"><Link to={`/desk/stories/${id}/edit`}><Pencil className="h-4 w-4 mr-1" /> Edit</Link></Button>
        )}
      </PageHead>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          {s.feedback && ["rejected", "on_hold", "withdrawn", "approved"].includes(s.status) && (
            <div data-testid="review-feedback" className="border-l-4 border-orange-500 bg-orange-50 p-4 text-sm">
              <p className="font-bold text-orange-800 uppercase text-[11px] tracking-[0.1em]">Desk feedback</p>
              <p className="mt-1 text-orange-900">{s.feedback}</p>
            </div>
          )}
          {s.missing?.length > 0 && (
            <div data-testid="review-missing" className="flex gap-2 border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> Cannot be submitted or published until complete. Missing: {s.missing.join(", ")}
            </div>
          )}
          <div className="bg-white border border-zinc-200">
            {s.cover_image && <img src={imgUrl(s.cover_image)} alt="" className="w-full aspect-[21/9] object-cover" data-testid="review-cover" />}
            <div className="flex border-b border-zinc-200 px-5">
              {["en", "ta"].map((l) => (
                <button key={l} data-testid={`review-tab-${l}`} onClick={() => setLang(l)}
                  className={`px-4 py-3 text-sm font-semibold -mb-px border-b-2 ${tabLang === l ? "border-zinc-900" : "border-transparent text-zinc-500"}`}>
                  {l === "en" ? "English" : "தமிழ் Tamil"}
                </button>
              ))}
            </div>
            <div className="p-5 sm:p-8"><Preview s={s} lang={tabLang} /></div>
            {s.images?.length > 0 && (
              <div className="grid grid-cols-3 gap-2 p-5 border-t border-zinc-200">
                {s.images.map((u) => <img key={u} src={imgUrl(u)} alt="" className="aspect-video object-cover w-full" />)}
              </div>
            )}
          </div>
        </div>
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-zinc-200 p-5 space-y-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-zinc-500">Status</span><StatusChip status={s.status} testId="review-status-chip" /></div>
            <div className="flex items-center justify-between"><span className="text-zinc-500">Trend</span><TrendBadge trend={s.trend_type} testId="review-trend-badge" /></div>
            {s.status === "published" && s.trend_expires_at && s.trend_type !== "regular" && (
              <div className="flex justify-between"><span className="text-zinc-500">Trend timer</span><span data-testid="review-trend-timer" className="font-mono-meta">{timeLeft(s.trend_expires_at)}</span></div>
            )}
            {s.scheduled_at && <div className="flex justify-between"><span className="text-zinc-500">Scheduled</span><span data-testid="review-scheduled-at" className="font-mono-meta text-purple-700">{fmtDate(s.scheduled_at)}</span></div>}
            {s.published_at && <div className="flex justify-between"><span className="text-zinc-500">Published</span><span className="font-mono-meta">{fmtDate(s.published_at)}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-500">Category</span><span>{cat?.name_en || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Reported by</span><span>{s.reported_by || "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Tags</span><span className="text-right">{s.tags?.join(", ") || "—"}</span></div>
          </div>
          {available.length > 0 && (
            <div className="bg-white border border-zinc-200 p-5" data-testid="review-actions">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-600 mb-3">Actions</p>
              <div className="flex flex-col gap-2">
                {available.map(([k, a]) => (
                  <Button key={k} data-testid={`action-${k}-btn`} disabled={busy} onClick={() => run(k)}
                    variant={a.danger ? "outline" : k === "publish" || k === "approve" || k === "submit" ? "default" : "outline"}
                    className={`rounded-sm justify-start ${a.danger ? "text-red-700 border-red-200 hover:bg-red-50" : ""}`}>
                    {a.label}
                  </Button>
                ))}
              </div>
              {!approver && s.author_role === "reporter" && <p className="text-xs text-zinc-500 mt-3">Approvers will review your story and you'll be notified by email.</p>}
            </div>
          )}
          <div className="bg-white border border-zinc-200 p-5" data-testid="review-history">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-600 mb-3">History</p>
            <ol className="space-y-3 border-l border-zinc-200 pl-4">
              {hist.map((h) => (
                <li key={h.id} className="text-sm relative">
                  <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-zinc-900" />
                  <p className="font-semibold">{h.action.replace("story.", "").replace(/_/g, " ")}</p>
                  <p className="text-xs text-zinc-500">{h.actor_name} · {fmtDate(h.created_at)}</p>
                  {h.details && <p className="text-xs text-zinc-600 mt-0.5">{h.details}</p>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="bg-white" data-testid="action-dialog">
          <DialogHeader><DialogTitle>{da?.label}</DialogTitle></DialogHeader>
          {da?.input === "feedback" && (
            <Textarea data-testid="action-feedback-input" placeholder={da.required ? "Feedback for the reporter (required)" : "Note (optional)"}
              value={input.feedback} onChange={(e) => setInput({ ...input, feedback: e.target.value })} className="min-h-[110px]" />
          )}
          {da?.input === "datetime" && (
            <Input data-testid="action-schedule-input" type="datetime-local" value={input.scheduled_at} onChange={(e) => setInput({ ...input, scheduled_at: e.target.value })} />
          )}
          {da?.input === "hours" && (
            <div>
              <Input data-testid="action-hours-input" type="number" min={1} value={input.hours} onChange={(e) => setInput({ ...input, hours: e.target.value })} />
              <p className="text-xs text-zinc-500 mt-1">Hours to add to the {s.trend_original} timer.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)} data-testid="action-cancel-btn">Cancel</Button>
            <Button data-testid="action-confirm-btn" disabled={busy || (da?.required && !input.feedback.trim()) || (da?.input === "datetime" && !input.scheduled_at)} onClick={() => run(dlg)}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
